import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import { Feature, UserModalComponent, UserModalConfig, UserModalUpdate } from 'src/app/components/user-modal/user-modal.component';
import { UserMultiModalComponent, UserMultiModalResult } from 'src/app/components/user-multi-modal/user-multi-modal.component';
import { Columns } from 'src/app/components/user-page-view/user-page-view.component';
import { AlertService } from 'src/app/services/alert.service';
import { CmsService } from 'src/app/services/cms.service';
import { Licence, Role } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-licence',
  templateUrl: './licence.component.html',
  styleUrls: ['./licence.component.scss']
})
export class LicenceComponent implements OnInit, OnDestroy {
  public entries: Licence[] = [];
  public columns: Columns = {
    uuid: false,
    pattern: true,
    licence: true,
    usage: true,
    activations: true,
    labels: false,
    roles: true,
    registered: null,
    created: false,
    expiry: true
  };
  public clearSelection = new Subject<void>();
  public clearSelectionChange: Observable<void>;

  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Delete));
  }

  public get roles(): BehaviorSubject<Role[]> {
    return this.users.roles;
  }

  private subscription: Subscription;

  constructor(
    private modal: NgbModal,
    private alert: AlertService,
    private toastr: ToastrService,
    private users: UserService,
    private cms: CmsService
  ) {}

  public ngOnInit() {
    this.clearSelectionChange = this.clearSelection.asObservable();
    this.subscription = this.users.templates.subscribe(entries => {
      this.entries = entries.filter(e => e.key != null);
      this.columns.username = this.entries.some(e => e.username != null) ? true : null;
      this.columns.pattern = this.entries.some(e => e.pattern != null) || this.entries.length == 0 ? true : null;
      this.columns.licence = this.entries.some(e => e.key != null) || this.entries.length == 0 ? true : null;
      this.columns.mail = this.entries.some(e => e.profile.meta?.mail != null) ? true : null;
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public copy(item) {
    return JSON.parse(JSON.stringify(item));
  }

  public openCreateModal() {
    const modalRef = this.modal.open(UserModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef);
    modalRef.componentInstance.config = this.modalConfig(false, true);
    modalRef.componentInstance.onSubmit.subscribe((data: UserModalUpdate) => {
      this.create(data.model);
    });
  }

  public openUpdateModal(template: Licence) {
    const modalRef = this.modal.open(UserModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef);
    modalRef.componentInstance.config = this.modalConfig(true, template.username == null);
    modalRef.componentInstance.model = this.copy(template);

    modalRef.componentInstance.onSubmit.subscribe((data: UserModalUpdate) => {
      Object.assign(template, data.model);
      this.update(template);
    });
  }

  public openUpdateMultiModal(templates: Licence[]) {
    const modalRef = this.modal.open(UserMultiModalComponent, { size: 'lg' });
    this.putRolesAndCleanUp(modalRef, () => this.clearSelection.next());
    modalRef.componentInstance.config = {
      title: `Update ${templates.length} Entries`,
      state: true,
      duration: true
    };
    modalRef.componentInstance.onSubmit.subscribe((res: UserMultiModalResult) => {
      return Promise.all(
        templates.map(e => {
          if (res.toggleEnabled) e.profile.enabled = res.enabled;
          if (res.toggleRoles) e.profile.meta.roles = res.roles;
          if (res.toggleExpiration) e.profile.meta.expiration = res.expiration;
          if (res.toggleDuration) e.profile.meta.duration = res.duration; // has no duration
          return this.update(e);
        })
      );
    });
  }

  // Pipe information about all available roles into the modal.
  // Optionally perform the cleanup command IF the modal was closed successfully.
  private putRolesAndCleanUp(modalRef: NgbModalRef, cleanup: () => void = null) {
    const rolesSub = this.roles.subscribe(r => (modalRef.componentInstance.roles = r));
    modalRef.result.then(
      () => {
        rolesSub?.unsubscribe();
        if (cleanup) cleanup();
      },
      () => rolesSub?.unsubscribe()
    );
  }

  private modalConfig(update: boolean, pattern: boolean): UserModalConfig {
    return { header: 'Licence', update, state: false, settings: false, template: true, licence: Feature.required, pattern };
  }

  public reload(): Promise<any> {
    this.entries = [];
    return this.users.loadTemplates();
  }

  public create(template: Licence): Promise<any> {
    if (this.entries.find(entry => entry.key && entry.key === template.key)) {
      return this.alert.fireError('Already exists', 'A License entry with the same key already exists.');
    }
    return this.users.createLicense(template).then(() => this.toastr.success(`Licence "${template.key}" created`));
  }

  public update(template: Licence): Promise<any> {
    if (this.entries.find(entry => entry.uuid !== template.uuid && entry.key && entry.key === template.key)) {
      return this.alert.fireError('Already exists', 'A License entry with the same key already exists.');
    }
    return this.users.updateLicense(template).then(() => this.toastr.success(`Licence "${template.key}" updated`));
  }

  public delete(template: Licence): Promise<any> {
    return this.users.deleteLicense(template).then(() => this.toastr.success(`Licence "${template.key}" deleted`));
  }

  public deleteMulti(templates: Licence[]): Promise<any> {
    return Promise.all(templates.map(e => this.delete(e)));
  }
}
